// @flow

import { combineReducers } from 'redux';
import * as Immutable from 'immutable';
import {
  FETCH_THREADS,
  FETCH_THREADS_SUCCESS,
  FETCH_THREADS_FAILURE,
  FILTER_THREADS_SEARCH,
  FILTER_THREADS_SEARCH_THROTTLE,
  FILTER_THREADS_UNREAD,
  FILTER_THREADS_TRACKED,
  FILTER_THREADS_SUCCESS,
  FILTER_THREADS_FAILURE,
  TRACK_THREAD_SUCCESS,
  READ_THREAD,
  READ_THREAD_SUCCESS,
  READ_THREAD_FAILURE,
  CREATE_THREAD_SUCCESS,
} from '../actions/threads';
import { FETCH_NEW_MESSAGES_SUCCESS } from '../actions/messages';
import type { State } from './index';
import type { Thread } from '../types';

export type FilterState = {
  search: ?string,
  unread: boolean,
  tracked: boolean,
};

export type ThreadsState = {
  isFetching: boolean,
  isFiltering: boolean,
  isReading: boolean,
  isError: boolean,
  byId: Array<number>,
  items: Immutable.Map<string, Thread>,
  filter: FilterState,
};

const items = (state = Immutable.Map(), action) => {
  switch (action.type) {
    case FETCH_THREADS_SUCCESS:
    case FILTER_THREADS_SUCCESS:
      return Immutable.Map(action.response.entities.threads);
    case TRACK_THREAD_SUCCESS:
      return state.update(action.thread.toString(), t => ({
        ...t,
        tracked: action.value,
      }));
    case READ_THREAD_SUCCESS:
      return state.merge(Immutable.Map(action.response.entities.threads));
    case FETCH_NEW_MESSAGES_SUCCESS:
      return state.update(action.thread.toString(), t => ({
        ...t,
        unreadCount: t.unreadCount + action.diff,
        lastMessage: action.lastMessage,
      }));
    case CREATE_THREAD_SUCCESS:
      return state.set(action.thread.id.toString(), action.thread);
    default:
      return state;
  }
};

const byId = (state = [], action) => {
  switch (action.type) {
    case FETCH_THREADS_SUCCESS:
    case FILTER_THREADS_SUCCESS:
      return action.response.result;
    case CREATE_THREAD_SUCCESS:
      return [...state, action.thread.id];
    default:
      return state;
  }
};

const isFetching = (state = false, action) => {
  switch (action.type) {
    case FETCH_THREADS:
      return true;
    case FETCH_THREADS_SUCCESS:
    case FETCH_THREADS_FAILURE:
      return false;
    default:
      return state;
  }
};

const isFiltering = (state = false, action) => {
  switch (action.type) {
    case FILTER_THREADS_TRACKED:
    case FILTER_THREADS_UNREAD:
    case FILTER_THREADS_SEARCH_THROTTLE:
      return true;
    case FILTER_THREADS_SUCCESS:
    case FILTER_THREADS_FAILURE:
      return false;
    default:
      return state;
  }
};

const isReading = (state = false, action) => {
  switch (action.type) {
    case READ_THREAD:
      return true;
    case READ_THREAD_SUCCESS:
    case READ_THREAD_FAILURE:
      return false;
    default:
      return state;
  }
};

const isError = (state = null, action) => {
  switch (action.type) {
    case FETCH_THREADS_FAILURE:
    case FILTER_THREADS_FAILURE:
      return true;
    default:
      return state;
  }
};

const initialFilterState: FilterState = {
  unread: false,
  tracked: false,
  search: undefined,
};

const filter = (state: FilterState = initialFilterState, action) => {
  switch (action.type) {
    case FILTER_THREADS_SEARCH:
      return {
        ...state,
        search: action.value,
      };
    case FILTER_THREADS_UNREAD:
      return {
        ...state,
        unread: action.value,
      };
    case FILTER_THREADS_TRACKED:
      return {
        ...state,
        tracked: action.value,
      };
    default:
      return state;
  }
};

export default combineReducers({
  items,
  byId,
  isFetching,
  isReading,
  isFiltering,
  isError,
  filter,
});

export const getIsFetching = (state: State) => state.threads.isFetching;
export const getIsError = (state: State) => state.threads.isError;
export const getFilter = (state: State) => state.threads.filter;
export const getIsFiltering = (state: State) => state.threads.isFiltering;
export const getIsReading = (state: State) => state.threads.isReading;

export const getThreads = (state: State) => {
  const { byId, items } = state.threads;

  return Immutable.List(byId.map(id => items.get(id.toString())));
};

export const getThread = (state: State, id: string) => {
  return state.threads.items.get(id);
};



// WEBPACK FOOTER //
// ./src/modules/messaging/reducers/threads.js